// src/pages/QrPage.js
import React, { useState, useEffect, useMemo } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import KakaoAdFit from "../components/KakaoAdFit"; 
import "./QrPage.css";

function App() {
  const [url, setUrl] = useState("");
  const [qrValue, setQrValue] = useState("");
  const [darkMode, setDarkMode] = useState(false);

  // 옵션(필요 최소한)
  const [size, setSize] = useState(224); // 128~512
  const [fgColor, setFgColor] = useState("#111827");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [includeMargin, setIncludeMargin] = useState(true);

  // CSR 기준 canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "QR 코드 생성기",
      description:
        "주소를 입력해 QR 코드를 생성하고 PNG로 다운로드할 수 있는 TooLoves의 QR 코드 생성기",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "QR, QR 코드, qrcode.react, QR PNG, QR 다운로드, TooLoves, 웹 유틸",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "KRW",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // 다크/라이트 모드에 따라 브라우저 및 모바일 네비게이션 바 색상 변경
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  const handleGenerate = (e) => {
    e?.preventDefault?.();
    const v = url.trim();
    if (!v) {
      alert("웹 주소를 입력해주세요.");
      return;
    }
    setQrValue(v);
  };

  const handleDownload = () => {
    const canvas = document.getElementById("qr-canvas");
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = "qrcode.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setUrl("");
    setQrValue("");
  };

  // 공유 버튼 클릭 시 네이티브 공유 시트 열기
  const handleShare = async () => {
    if (!qrValue) return;

    const isShareCanceled = (err) => {
      if (!err) return false;
      const name = err.name || "";
      const msg = (err.message || "").toLowerCase();
      // 브라우저별 취소 오류 명/메시지 케이스 흡수
      return (
        name === "AbortError" ||
        name === "NotAllowedError" ||
        msg.includes("abort") ||
        msg.includes("cancel")
      );
    };

    const canvas = document.getElementById("qr-canvas");

    // 1) 파일 공유 시도 (가능하면 PNG 파일로)
    if (canvas && "toBlob" in canvas && typeof navigator?.canShare === "function") {
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (blob) {
        const file = new File([blob], "qrcode.png", { type: "image/png" });
        const shareDataWithFile = {
          files: [file],
          title: "QR 코드",
          text: qrValue,
        };

        if (navigator.canShare(shareDataWithFile)) {
          try {
            await navigator.share(shareDataWithFile);
            return; // 성공
          } catch (err) {
            if (isShareCanceled(err)) return; // 취소면 조용히 종료
            // 그 외 오류는 다음 폴백으로 진행
          }
        }
      }
    }

    // 2) 파일 공유가 안 되면: URL 공유 시도
    if (navigator.share) {
      try {
        await navigator.share({
          title: "QR 코드",
          text: "아래 링크로 이동하세요.",
          url: qrValue,
        });
        return; // 성공
      } catch (err) {
        if (isShareCanceled(err)) return; // 취소면 조용히 종료
        // 그 외 오류는 다음 폴백으로 진행
      }
    }

    // 3) 최종 폴백: URL 클립보드 복사
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(qrValue);
        alert("공유 시트를 지원하지 않아 링크를 클립보드에 복사했어요.");
      } else {
        alert("이 브라우저에서는 공유를 지원하지 않습니다.");
      }
    } catch {
      alert("이 브라우저에서는 공유를 지원하지 않습니다.");
    }
  };

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "ko" }}>
        <title>QR 코드 생성기 | TooLoves</title>
        <meta
          name="description"
          content="주소를 입력해 QR 코드를 생성하고 PNG로 다운로드할 수 있는 QR 코드 생성기"
        />
        <meta
          name="keywords"
          content="QR, QR 코드, qrcode.react, QR PNG, QR 다운로드, TooLoves, 웹 유틸"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 초기 theme-color, 런타임에서 useEffect로 동기화 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="QR 코드 생성기 | TooLoves" />
        <meta
          property="og:description"
          content="웹 주소로 QR 코드를 빠르게 만들고 PNG로 저장"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-qr.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="QR 코드 생성기 | TooLoves" />
        <meta
          name="twitter:description"
          content="웹 주소로 QR 코드를 빠르게 만들고 PNG로 저장"
        />
        <meta name="twitter:image" content="/og-qr.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* 상단 앱바 */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/" className="brand-link" aria-label="damoa 홈으로 이동">
            TooLoves
          </Link>
        <button
            className="mode-toggle"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "라이트 모드" : "다크 모드"}
          </button>
        </div>
      </header>

      {/* 본문 */}
      <main className="container">
        {/* ✅ 기존 광고 위치: 제거됨 */}
        <section className="generator-card">
          <h2 className="card-title">QR 코드 생성기</h2>
          <p className="card-subtitle">
            주소를 입력하고 생성하면 아래에서 미리보기와 다운로드가 가능합니다.
          </p>

          <form className="form" onSubmit={handleGenerate}>
            <label htmlFor="url" className="form-label sr-only">
              웹 주소
            </label>
            <div className="input-row">
              <input
                id="url"
                type="text"
                placeholder="여기에 웹사이트 주소를 입력"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoComplete="off"
                inputMode="url"
              />
              <button type="submit" className="generate-btn">
                QR 생성
              </button>
            </div>

            <div className="options-row">
              <div className="field">
                <label className="form-label">사이즈</label>
                <input
                  type="range"
                  min="128"
                  max="512"
                  step="32"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                />
                <div className="hint">{size}px</div>
              </div>

              <div className="field">
                <label className="form-label">전경색</label>
                <input
                  type="color"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                />
              </div>

              <div className="field">
                <label className="form-label">배경색</label>
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                />
              </div>

              <div className="field switch">
                <label className="form-label">여백 포함</label>
                <input
                  type="checkbox"
                  checked={includeMargin}
                  onChange={(e) => setIncludeMargin(e.target.checked)}
                />
              </div>
            </div>
          </form>

          <div className="preview-card">
            {qrValue ? (
              <>
                <div className="qr-wrap">
                  <QRCodeCanvas
                    id="qr-canvas"
                    value={qrValue}
                    size={size}
                    bgColor={bgColor}
                    fgColor={fgColor}
                    includeMargin={includeMargin}
                  />
                </div>
                <p className="qr-url">{qrValue}</p>
                <div className="actions">
                  <button type="button" onClick={handleDownload}>
                    PNG 다운로드
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleReset}
                  >
                    초기화
                  </button>

                  {/* 공유 아이콘 버튼 (텍스트 없음) */}
                  <button
                    type="button"
                    className="btn-secondary icon-btn"
                    onClick={handleShare}
                    aria-label="공유"
                    title="공유"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      {/* Feather/Lucide share-2 아이콘 */}
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                  </button>
                </div>
              </>
            ) : (
              <p className="placeholder">
                주소를 입력하고 “QR 생성”을 눌러주세요.
              </p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="법적 고지 링크">
          <Link to="/terms" className="footer-link">이용약관</Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to="/privacy" className="footer-link">개인정보처리방침</Link>
        </nav>
      </footer>

      {/* ✅ 광고: 푸터 밑으로 이동 */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', paddingBottom: '20px' }}>
        <KakaoAdFit />
      </div>
    </div>
  );
}

export default App;